// 平台数据是默认来源；静态数据只有显式选择后才参与渲染。
const states = { online: '在线', offline: '离线', notActive: '未激活', unknown: '未知' }
const isObject = value => value && typeof value === 'object' && !Array.isArray(value)
const validId = value => typeof value === 'string' && value.trim() && value.length <= 256

// 数据编辑和画布渲染使用同一份校验，避免错误数据被悄悄当作空列表。
export function validateListData (kind, data, maxRows = 1000) {
  if (!Array.isArray(data)) return '数据必须是 JSON 数组'
  if (data.length > maxRows) return '静态数据最多支持 ' + maxRows + ' 条'
  const ids = new Set()
  for (let i = 0; i < data.length; i++) {
    const row = data[i], at = '第 ' + (i + 1) + ' 条：'
    if (!isObject(row)) return at + '必须是对象'
    if (!validId(row.id)) return at + 'id 必须是非空字符串，最多 256 字符'
    if (ids.has(row.id)) return at + 'id 重复'
    ids.add(row.id)
    if (typeof row.name !== 'string' || !row.name.trim()) return at + 'name 必须是非空字符串'
    if (kind === 'device') {
      if (!validId(row.productId)) return at + 'productId 必须是非空字符串，最多 256 字符'
      if (row.productName !== undefined && typeof row.productName !== 'string') return at + 'productName 必须是字符串'
      if (row.state !== undefined && !Object.prototype.hasOwnProperty.call(states, row.state)) return at + 'state 只能为 online / offline / notActive / unknown'
      if (row.lastSeen !== undefined && typeof row.lastSeen !== 'string') return at + 'lastSeen 必须是字符串'
    } else {
      for (const field of ['deviceCount', 'onlineCount', 'alarmCount']) {
        if (row[field] !== undefined && (!Number.isSafeInteger(row[field]) || row[field] < 0)) return at + field + ' 必须是非负整数'
      }
      if (row.deviceCount !== undefined && row.onlineCount > row.deviceCount) return at + 'onlineCount 不能大于 deviceCount'
    }
  }
  return ''
}

export function productChoices (kind, data) {
  if (validateListData(kind, data)) return []
  const products = new Map()
  data.forEach(row => {
    const id = kind === 'product' ? row.id : row.productId
    if (!products.has(id)) products.set(id, { id, name: kind === 'product' ? row.name : (row.productName || id) })
  })
  return Array.from(products.values())
}

export function listState (kind, data, option = {}, context = {}, editing = false) {
  const error = validateListData(kind, data, usesPlatformData(option) ? Infinity : 1000)
  if (error) return { rows: [], error, empty: '', scope: '数据格式错误' }
  let rows = data, scope = '全部产品', empty = '暂无' + (kind === 'product' ? '产品' : '设备') + '数据'
  if (kind === 'product' && option.productScope === 'specified') {
    const ids = Array.isArray(option.productIds) ? option.productIds : []
    rows = rows.filter(row => ids.includes(row.id))
    scope = '已选 ' + ids.length + ' 个产品'
    empty = ids.length ? '所选产品不存在或无访问权限' : '尚未选择要显示的产品'
  } else if (kind === 'device' && option.productScope !== 'all') {
    const current = option.productScope === 'current'
    const id = selectedProductId(option, context, editing)
    rows = rows.filter(row => row.productId === id)
    const product = rows.find(row => row.productId === id)
    scope = current ? (editing ? '当前产品（编辑预览）' : '当前页面产品') : (product ? product.productName || id : '指定产品')
    empty = id ? '该产品下暂无设备' : (current && !editing ? '当前页面没有产品参数' : '尚未选择产品')
  }
  return { rows, error: '', empty, scope }
}

export function deviceState (state) { return states[state] || states.unknown }

export function usesPlatformData (option = {}) { return option.dataSource !== 'static' }

export function selectedProductId (option = {}, context = {}, editing = false) {
  if (option.productScope !== 'current') return option.productId
  const id = editing ? option.previewProductId : context.productId
  return id || (option.dataSource === 'static' ? option.demoProductId : undefined)
}

// 每次调用都创建独立数组和样式配置，拖入后的实例不共享编辑状态。
export function createListComponent (kind, dataSource = 'platform') {
  const names = ['智能照明控制器', '环境监测终端', '中央空调控制器', '智能电表', '门禁控制器', '水浸传感器', '烟雾报警器', '智能插座', '温湿度传感器', '停车检测终端', '能耗采集网关', '空气质量监测终端']
  const products = names.map((name, i) => ({
    id: i === 0 ? 'demo_lighting' : 'demo_product_' + (i + 1), name,
    deviceCount: i < 3 ? 12 : 24 + i * 3, onlineCount: i < 3 ? 9 : 22 + i * 2, alarmCount: i % 4 === 1 ? 2 : 0
  }))
  const data = kind === 'product' ? products : products.slice(0, 3).flatMap((product, p) => Array.from({ length: 12 }, (_, i) => ({
    id: 'demo_device_' + (p + 1) + '_' + (i + 1),
    name: ['一楼东区', '二楼办公区', '地下停车场', '南门通道'][i % 4] + product.name + ' ' + (i + 1),
    productId: product.id, productName: product.name,
    state: i % 5 === 3 ? 'offline' : (i % 7 === 6 ? 'notActive' : 'online'),
    lastSeen: i % 7 === 6 ? '暂无上报' : (i % 5 === 3 ? '8 分钟前' : '2 秒前')
  })))
  const label = kind === 'product' ? '产品列表' : '设备列表', prop = kind + 'List'
  return {
    name: label, title: label, icon: kind === 'product' ? 'el-icon-box' : 'el-icon-monitor',
    dataType: 0, time: 0, data: dataSource === 'static' ? data : [],
    component: { width: 560, height: 360, name: prop, prop },
    option: {
      title: label, showTitle: true, showId: true, showCount: true, dataSource,
      showSearch: false, showPagination: false, pageSize: 20,
      productScope: kind === 'product' ? 'all' : 'specified', productIds: [],
      productId: dataSource === 'static' ? 'demo_lighting' : '', previewProductId: dataSource === 'static' ? 'demo_lighting' : '',
      rowHeight: kind === 'product' ? 112 : 78, fontSize: 16, color: '#e0f3ff', secondaryColor: '#91b6cf',
      backgroundColor: '#042035', borderColor: '#1c526e', separatorColor: '#1c526e', onlineColor: '#36e6b3', offlineColor: '#ff737d'
    }
  }
}
